"use client"
import { graphql, useQuery } from "@opensea/graphql"
import { useCallback, useState } from "react"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks/useAddress"
import type {
  CancelListingsForItemsFlowState,
  CancelOrderFlowState,
  CancelOrderIdFlowState,
} from "../../flow-state"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import { CancelOrderFlowContent } from "./CancelOrderFlowContent"
import { CancelOrderFlowContentFragment } from "./CancelOrderFlowContent.fragment"
import { CancelOrderFlowSkeleton } from "./CancelOrderFlowSkeleton"

type Props = CancelOrderFlowState

const CancelOrderIdsFlow = ({ orderIds }: CancelOrderIdFlowState) => {
  const address = useAddress()
  const [gaslessCancelEligibleOrders, setGaslessCancelEligibleOrders] =
    useState(true)

  const handleForceOnchainCancellation = useCallback(() => {
    setGaslessCancelEligibleOrders(false)
  }, [])

  const [{ data, fetching }] = useQuery({
    query: graphql(
      `
        query CancelOrderIdsFlowQuery(
          $orderIds: [String!]!
          $address: Address!
          $gaslessCancelEligibleOrders: Boolean!
        ) {
          cancelOrders(
            orderIds: $orderIds
            address: $address
            gaslessCancelEligibleOrders: $gaslessCancelEligibleOrders
          ) {
            ...CancelOrderFlowContent
          }
        }
      `,
      [CancelOrderFlowContentFragment],
    ),
    variables: {
      orderIds,
      address: address ?? "",
      gaslessCancelEligibleOrders,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
    pause: !address,
  })

  if (fetching) {
    return <CancelOrderFlowSkeleton count={orderIds.length} />
  }

  if (!data) {
    throw new Error("There was a problem with cancelling.")
  }

  return (
    <CancelOrderFlowContent
      data={data.cancelOrders}
      gaslessCancelEligibleOrders={gaslessCancelEligibleOrders}
      onForceOnchainCancellation={handleForceOnchainCancellation}
    />
  )
}

const CancelListingsForItemsFlow = ({
  items,
}: CancelListingsForItemsFlowState) => {
  const address = useAddress()
  const [{ data, fetching }] = useQuery({
    query: graphql(
      `
        query CancelListingsForItemsFlowQuery(
          $items: [ItemIdentifierInput!]!
          $address: Address!
        ) {
          cancelItemsListings(items: $items, address: $address) {
            ...CancelOrderFlowContent
          }
        }
      `,
      [CancelOrderFlowContentFragment],
    ),
    variables: {
      items: items.map(({ chain, contractAddress, tokenId }) => ({
        chain: chain.identifier,
        contractAddress,
        tokenId,
      })),
      address: address as string,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  if (fetching) {
    return <CancelOrderFlowSkeleton count={items.length} />
  }

  if (!data) {
    throw new Error("There was a problem with cancelling.")
  }

  return <CancelOrderFlowContent data={data.cancelItemsListings} />
}

export const CancelOrderFlow = (props: Props) => {
  useEndFlowOnAccountChange()

  if ("orderIds" in props) {
    return <CancelOrderIdsFlow {...props} />
  }

  return <CancelListingsForItemsFlow {...props} />
}
